<template>
  <div class="w-full px-3 h-full">
    <ImpersonateUsers :campaign="campaign" />
    <h2 class="font-bold text-18px pb-0.5 py-3">Summary</h2>
    <el-scrollbar class="h-[calc(100%_-_80px)] summary-custom-scroll">
      <div v-if="campaign">
        <SummaryCard label="Client">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#crown`"></use>
            </svg>
          </template>
          <span>{{ campaign?.client?.name || '-' }}</span>
        </SummaryCard>
        <!-- <SummaryCard :label="isSimple ? 'Budget' : 'Total Budget'">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#money`"></use>
            </svg>
          </template>
          <span>{{ budget === 0 ? '-' : `&euro;${budget}` }}</span>
        </SummaryCard> -->
        <!-- <SummaryCard label="CPM">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#money`"></use>
            </svg>
          </template>
          <span>{{ campaign?.cpm ? `&euro;${campaign.cpm}` : '-' }}</span>
        </SummaryCard> -->
        <SummaryCard label="Period">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#calendar`"></use>
            </svg>
          </template>
          <el-tooltip effect="dark" :content="period" placement="top-start">
            <div class="truncate max-w-[142px]">{{ period }}</div>
          </el-tooltip>
        </SummaryCard>
        <SummaryCard label="Impressions">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#eyeImp`"></use>
            </svg>
          </template>
          <div v-if="budget" class="w-[87px] flex justify-between items-center">
            <div class="w-[48px] h-[4px] rounded bg-gray"></div>
            <span> {{ impressions }}</span>
          </div>
          <span v-else> {{ impressions }}</span>
        </SummaryCard>
        <SummaryCard label="Inventory">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#globe`"></use>
            </svg>
          </template>
          <span>{{ campaign?.publisher?.name || '-' }}</span>
        </SummaryCard>
        <SummaryCard label="Assets">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#doc`"></use>
            </svg>
          </template>
          <span>{{ totalAssets }}</span>
        </SummaryCard>
        <SummaryCard label="Creatives">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#files`"></use>
            </svg>
          </template>
          <span>{{ totalCreatives }}</span>
        </SummaryCard>
        <SummaryCard label="Screenshots">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#screen`"></use>
            </svg>
          </template>
          <span>{{ totalScreenshots }}</span>
        </SummaryCard>
        <SummaryCard label="Observations" :type="campaign.observations ? 'full' : 'flex'">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#comment`"></use>
            </svg>
          </template>
          <div>
            {{ formattedObservations || '-' }}
            <el-button v-if="hasLongText" class="p-0 m-0" type="text" @click="toggleExpand">{{ isExpanded ? '[less]' : 'more' }}</el-button>
          </div>
        </SummaryCard>
        <SummaryCard label="Media Plan" :type="campaign.media_plan.length === 0 ? 'flex' : 'full'">
          <template slot="icon">
            <svg width="16" height="17">
              <use :xlink:href="`#media-plan`"></use>
            </svg>
          </template>
          <div
            v-if="campaign.media_plan.length > 0"
            v-for="(item, index) in campaign.media_plan"
            :key="index"
            class="flex items-center justify-between mt-2"
          >
            <div class="flex items-center gap-1">
              <svg width="16" height="17">
                <use :xlink:href="`#doc`"></use>
              </svg>
              <span>{{ item.name }}</span>
            </div>

            <a :href="`/api/social-products/social-boost/download/${item.id}`" class="text-black">
              <svg width="16" height="17">
                <use :xlink:href="`#slim-download`"></use>
              </svg>
            </a>
          </div>
          <span v-if="campaign.media_plan.length === 0">-</span>
        </SummaryCard>
      </div>
    </el-scrollbar>
  </div>
</template>
<script>
import { evaluate, forEach } from 'mathjs'
import { mapGetters } from 'vuex'
import SummaryCard from '@/components/social-products/social-boost/parts/SummaryCard.vue'
import { FormatDate } from '@/utils'
import ImpersonateUsers from '@/components/social-products/social-boost/parts/ImpersonateUsers/ImpersonateUsers.vue'

export default {
  name: 'Summary',
  components: {
    SummaryCard,
    ImpersonateUsers,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: false,
    }
  },
  computed: {
    ...mapGetters('socialBoostCampaign', ['campaign']),
    flight() {
      return this.campaign?.flights?.find((f) => f.id === Number(this.$route.params.flight_id))
    },
    period() {
      if (this.isSimple) {
        if (this.flight?.start_date && this.flight?.end_date) {
          return `${FormatDate(this.flight.start_date, this.flight.end_date)}`
        }
      } else if (this.campaign.flights.length > 0) {
        return this.$minMaxDate(this.campaign.flights)
      }
      return '-'
    },
    impressions() {
      const formula = '(budget / CPM) * 1000'
      if (this.budget) {
        const impresions = evaluate(formula, { budget: this.budget, CPM: this.campaign.cpm })
        var formatted = this.$numbro(impresions).format({
          average: true,
          thousandSeparated: true,
          mantissa: 1,
          roundingFunction: Math.floor,
        })
        formatted = formatted.replace(/([a-z])$/, (m) => m.toUpperCase())
        return formatted
      } else {
        return '-'
      }
    },
    isSimple() {
      return this.campaign.is_simple
    },
    totalBudget() {
      let totalBudget = 0
      forEach(this.campaign.flights, (flight) => {
        totalBudget = totalBudget + Number(flight.budget)
      })

      return totalBudget
    },
    budget() {
      if (this.isSimple) {
        return this.flight?.budget ? this.flight?.budget : 0
      }
      return this.totalBudget
    },
    totalAssets() {
      if (this.isSimple) {
        return this.flight?.assets?.length || '-'
      }
      let totalAssets = 0
      forEach(this.campaign.flights, (flight) => {
        totalAssets = totalAssets + Number(flight.assets.length)
      })

      return totalAssets === 0 ? '-' : totalAssets
    },
    totalCreatives() {
      if (this.isSimple) {
        // Count creatives in the current flight's assets
        let creatives = 0
        this.flight?.assets?.forEach((asset) => {
          creatives += asset.creatives?.length || 0
        })
        return creatives || '-'
      }
      // Count creatives across all flights
      let totalCreatives = 0
      forEach(this.campaign.flights, (flight) => {
        forEach(flight.assets, (asset) => {
          totalCreatives += asset.creatives?.length || 0
        })
      })
      return totalCreatives === 0 ? '-' : totalCreatives
    },
    totalScreenshots() {
      if (this.isSimple) {
        // Count screenshots in the current flight's assets
        let screenshots = 0
        this.flight?.assets?.forEach((asset) => {
          screenshots += asset.screenshots?.length || 0
        })
        return screenshots || '-'
      }
      // Count screenshots across all flights
      let totalScreenshots = 0
      forEach(this.campaign.flights, (flight) => {
        forEach(flight.assets, (asset) => {
          totalScreenshots += asset.screenshots?.length || 0
        })
      })
      return totalScreenshots === 0 ? '-' : totalScreenshots
    },
    formattedObservations() {
      const observations = this.campaign.observations

      if (!observations) {
        return false
      }

      return this.isExpanded ? observations : observations.substring(0, 88) + (observations.length > 88 ? '...' : '')
    },
    hasLongText() {
      return this.campaign.observations && this.campaign.observations.length > 88
    },
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded
    },
    async downloadFiles(id) {
      console.log('Download files', id)
      await this.axios.get(`/api/social-products/social-boost/download/${id}`)
    },
  },
}
</script>
<style lang="scss">
.summary-custom-scroll {
  .el-scrollbar__wrap {
    -ms-overflow-style: none;
    scrollbar-width: none;
    margin-right: 0 !important;
  }
  .el-scrollbar__wrap::-webkit-scrollbar {
    display: none;
  }
}
</style>
